<template>
  <div class="container is-fluid" ref="container">
    <section class="section">
      <h1 class="title">{{ $t("bnpl:prescore.title.list") }}</h1>
      <div class="buttons is-justify-content-end"></div>
      <FieldListing
        :data="bnplPrescores"
        :columns="tableColumns"
        :actions="actions"
        :searchable="true"
        :paginated="true"
        default-sort="created"
        store-fetch-command="bnplApplication/fetchPrescoreList"
        store-get-command="bnplApplication/listPrescores"
        :search-fields="searchFields"
        ref="dataTable"
      >
      </FieldListing>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import i18n from "@/plugins/i18n";
import { AUTHORITIES } from "@/utils/authorities";
import {
  BNPL_APPLICATION_STATUS_RESULT_COLORS,
  BNPL_APPLICATION_STATUS_RESULT_TOKENS,
  BNPL_APPLICATION_STATUS_TOKENS,
  BNPL_APPLICATION_STATUS,
  BNPL_FLOW_POSITIONS,
  SEARCH_FIELD_TYPES,
  FLOW_POSITIONS,
} from "@/utils/const";
import escapeHtml from "escape-html";
import { formatDateTime, localizedFormatter } from "@/utils/util";
import exportMixin from "@/mixins/exportMixin";

export default {
  name: "bnplPrescoresList",
  title: "pp:page.bnpl_prescore_list.title",
  mixins: [exportMixin],
  components: {
    FieldListing: () => import("@/components/FieldListing.vue"),
  },
  computed: {
    ...mapGetters({
      fileGetter: "drawer/getFile",
      country: "i18n/country",
    }),
    canSeebnplPrescoresExport() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.BNPL.APPLICATION.ADMIN,
        AUTHORITIES.BNPL.APPLICATION.EXPORT,
      ]);
    },
  },
  data() {
    return {
      bnplPrescores: [],
      BNPL_APPLICATION_STATUS,
      BNPL_APPLICATION_STATUS_TOKENS,
      BNPL_APPLICATION_STATUS_RESULT_TOKENS,
      BNPL_APPLICATION_STATUS_RESULT_COLORS,
      FLOW_POSITIONS,
      searchFields: [
        {
          name: "bnpl:prescore.list_header.status",
          identifier: "currentStatus",
          values: BNPL_APPLICATION_STATUS_TOKENS,
          type: SEARCH_FIELD_TYPES.ENUM,
          genericSearch: true,
        },
        {
          name: "bnpl:prescore.list_header.created",
          identifier: "created",
          type: SEARCH_FIELD_TYPES.DATETIME,
          genericSearch: false,
          defaultValue: `${
            new Date(new Date().setMonth(new Date().getMonth() - 6))
              .toISOString()
              .split("T")[0]
          } 00:00:00`,
        },
        {
          name: "bnpl:prescore.list_header.customer_full_name",
          identifier: "customerFullName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:prescore.list_header.customer_phone",
          identifier: "customerPhoneNumber",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:prescore.list_header.customer_email",
          identifier: "customerEmail",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:prescore.list_header.external_customer_phone",
          identifier: "externalCustomerPhoneNumber",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:prescore.list_header.external_customer_email",
          identifier: "externalCustomerEmail",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:prescore.list_header.id",
          identifier: "id",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: false,
        },
        {
          name: "bnpl:prescore.list_header.offer_name",
          identifier: "offerName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:prescore.list_header.offer_id",
          identifier: "offerId",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: false,
        },
        {
          name: "bnpl:prescore.list_header.partner_name",
          identifier: "partnerName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:prescore.list_header.partner_id",
          identifier: "partnerId",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: false,
        },
        {
          name: "bnpl:prescore.list_header.current_position",
          identifier: "currentPosition",
          type: SEARCH_FIELD_TYPES.ENUM,
          values: BNPL_FLOW_POSITIONS,
          genericSearch: false,
        },
        {
          name: "bnpl:prescore.list_header.deleted",
          identifier: "deleted",
          type: SEARCH_FIELD_TYPES.BOOLEAN,
        },
      ],
      tableColumns: [
        {
          field: "customerFullName",
          label: "bnpl:prescore.list_header.name",
          format: (row) => {
            if (row.customerFullName) {
              return row.customerFullName;
            }
            return "-";
          },
        },
        {
          field: "offerName",
          label: "bnpl:prescore.list_header.offer",
          vhtml: true,
          format: (row) => {
            return `
<div style="display: flex;">
  <div style="width: 90%">
    <p class="has-text-weight-medium mb-0">${escapeHtml(row.offerName)}</p>
    <span class="is-size-7 has-text-grey-icon">${escapeHtml(
      row.offerDescription
    )}</span>
  </div>
 </div>
`;
          },
        },
        {
          field: "currentStatus",
          label: "bnpl:prescore.list_header.status",
          vhtml: true,
          format: (row) => {
            return `<p
    class="has-text-weight-medium mb-0 has-text-grey-icon"
  >
    ${escapeHtml(i18n.t(BNPL_APPLICATION_STATUS_TOKENS[row.currentStatus]))}
  </p>
  <span class="is-size-7 ${
    BNPL_APPLICATION_STATUS_RESULT_COLORS[row.currentStatusResult]
  }">
    ${escapeHtml(
      i18n.t(BNPL_APPLICATION_STATUS_RESULT_TOKENS[row.currentStatusResult])
    )}</span
  >`;
          },
        },
        {
          field: "created",
          label: "bnpl:prescore.list_header.created",
          vhtml: true,
          format: (row) => {
            return `<p
    class="mb-0 ${
      row.currentStatus === BNPL_APPLICATION_STATUS.CANCELLED
        ? "has-text-grey-icon"
        : "has-text-weight-medium"
    }"  style="white-space: nowrap;"
  >
    ${localizedFormatter(this.country.languageIso).time(row.created)}
  </p>
  <span class="has-text-grey-icon" style="white-space: nowrap;">
    ${localizedFormatter(this.country.languageIso).date(row.created)}
  </span>`;
          },
        },
      ],
      actions: [
        {
          icon: "chevron-right",
          iconPack: "fa",
          type: "is-grey",
          function: (row) => {
            this.openBnplPrescore(row.id);
          },
        },
      ],
    };
  },
  methods: {
    ...mapActions({
      fetchFile: "drawer/download",
    }),
    async fetchList() {
      this.loaded = false;

      await this.$store.dispatch(
        "bnplApplication/fetchPrescoreList",
        "sort=created,desc"
      );
      this.bnplPrescores = this.$store.getters["bnplApplication/listPrescores"];
      this.loaded = true;
    },
    async exportDealCsv() {
      await this.downloadListExport(
        "container",
        "bnplApplication/exportDealCsv",
        "dataTable",
        `instacash-bnpl-deal-export.csv`
      );
    },
    async exportTransactionCsv() {
      await this.downloadListExport(
        "container",
        "bnplApplication/exportTransactionCsv",
        "dataTable",
        `instacash-bnpl-transaction-export.csv`
      );
    },
    async createImageUrl(documentId) {
      try {
        if (!this.fileGetter(documentId)) {
          await this.fetchFile(documentId);
        }

        let blob = this.fileGetter(documentId);
        if (blob) {
          return window.URL.createObjectURL(blob);
        }
      } catch (ex) {
        console.error(ex);
      }
      return null;
    },
    openBnplPrescore(id) {
      this.$router.push({
        name: "BnplPrescore",
        params: {
          given: id,
        },
      });
    },
    getDate(date, part) {
      // example: 2021.06.15. 13:44 where part=1 is the time
      const d = formatDateTime(date);
      return d.split(" ")[part];
    },
    newDeal() {
      this.$router.push({
        name: "NewApplication",
      });
    },
  },
};
</script>

<style scoped></style>
